import Icon from '../../../components/AppIcon';
import type { Capability } from '../types';

const CapabilitiesSection = () => {
  const capabilities: Capability[] = [
    {
      id: "1",
      icon: "MessageSquare",
      title: "Customer Support Automation",
      description: "Handle FAQs, troubleshooting, and common inquiries with intelligent responses that understand context and intent.",
      examples: [
        "Order status and tracking",
        "Product information and recommendations",
        "Return and refund processing",
        "Account management assistance"
      ]
    },
    {
      id: "2",
      icon: "ShoppingCart",
      title: "E-commerce Assistance",
      description: "Guide customers through the buying journey with personalized product recommendations and seamless checkout support.",
      examples: [
        "Product discovery and search",
        "Size and fit recommendations",
        "Cart abandonment recovery",
        "Upsell and cross-sell suggestions"
      ]
    },
    {
      id: "3",
      icon: "Calendar",
      title: "Appointment Scheduling",
      description: "Automate booking, rescheduling, and cancellations with calendar integration and reminder notifications.",
      examples: [
        "Real-time availability checking",
        "Automated confirmation emails",
        "Reminder notifications",
        "Waitlist management"
      ]
    },
    {
      id: "4",
      icon: "Users",
      title: "Lead Qualification",
      description: "Engage website visitors, qualify leads, and route high-value prospects to your sales team automatically.",
      examples: [
        "Visitor engagement and capture",
        "Budget and timeline qualification",
        "Meeting scheduling with sales",
        "CRM data enrichment"
      ]
    },
    {
      id: "5",
      icon: "FileText",
      title: "Document Processing",
      description: "Extract information from documents, answer questions about policies, and guide users through forms.",
      examples: [
        "Policy and FAQ navigation",
        "Form filling assistance",
        "Document verification",
        "Compliance checking"
      ]
    },
    {
      id: "6",
      icon: "Globe",
      title: "Multilingual Support",
      description: "Communicate with customers in their preferred language with real-time translation and cultural adaptation.",
      examples: [
        "50+ language support",
        "Automatic language detection",
        "Cultural context awareness",
        "Regional customization"
      ]
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Zap" size={16} />
            <span>Capabilities</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            What Our AI Chatbots Can Do
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            From customer support to lead generation, our chatbots handle diverse business needs
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((capability) => (
            <div
              key={capability.id}
              className="bg-card rounded-xl p-6 border border-border hover:shadow-lg transition-all duration-fast"
            >
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Icon name={capability.icon} size={28} className="text-primary" />
              </div>
              
              <h3 className="text-xl font-semibold text-text-primary mb-3">
                {capability.title}
              </h3>
              
              <p className="text-text-secondary mb-4 leading-relaxed">
                {capability.description}
              </p>
              
              <div className="space-y-2">
                <div className="text-sm font-medium text-text-tertiary mb-2">
                  Use Cases:
                </div>
                <ul className="space-y-2">
                  {capability.examples.map((example, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{example}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesSection;